import getFinanceTermsByTier from './utils/getFinanceTermsByTier.js';
import getDealerSupportedDealTypes from './utils/getDealerSupportedDealTypes.js';
import getTransformedSelectedCriteria from './utils/getTransformedSelectedCriteria.js';

/**
 * Applies the Dealer.com settings we retrieved for this dealer to the calculator configuration. This
 * includes deal type, negotiation, taxes and fees, selected criteria, and custom disclaimer.
 *
 * @param {Object} config - The existing calculator configuration object.
 * @param {Object} drSettings - The fetched dealer settings object.
 * @param {('New'|'Used')} vehicleType - The type of vehicle.
 * @param {Object} listing - The vehicle listing object with basic required information.
 * @returns {Object} The updated calculator configuration object with applied DR settings.
 */

async function applyDrSettings(config) {
  let drSettings = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  let vehicleType = arguments.length > 2 ? arguments[2] : undefined;
  let listing = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  const supportedDeals = getDealerSupportedDealTypes(drSettings, vehicleType, listing, config?.listingPrice);
  const {
    defaultDealType = 'finance',
    products = {},
    dealerSiteToggles = {}
  } = drSettings;
  const {
    isVDPLeadFormEnabled = true
  } = dealerSiteToggles;
  const showUDS = products[`hasWidget${vehicleType}`] ?? true;
  const fallBackDealType = supportedDeals?.length === 1 && supportedDeals[0] === 'cash' ? supportedDeals[0] : 'finance';
  return {
    ...config,
    supportedDealTypes: config.supportedDealTypes ?? supportedDeals,
    initialDealType: config.initialDealType ?? (supportedDeals.includes(defaultDealType) ? defaultDealType : fallBackDealType),
    enableNegotiation: config.enableNegotiation ?? drSettings[`canNegotiate${vehicleType}`],
    // Don't apply taxes and fees setting if ecommerce is Accelerate, but allow for other tenants
    enableTaxesAndFees: config.enableTaxesAndFees ?? (config.ecommerce === 'Accelerate' ? undefined : drSettings.applyTaxesAndFees),
    includeTaxesAndFees: config.includeTaxesAndFees ?? undefined,
    isDueAtSigningAmountDeduct: drSettings.leasing.isDueAtSigninAmountDeduct ?? false,
    selectedCriteria: getTransformedSelectedCriteria(config?.selectedCriteria, drSettings, vehicleType),
    oemLabels: config.ecommerce === 'Accelerate' ? drSettings.oemLabels : {},
    shortCustomDisclaimer: config.shortCustomDisclaimer ?? drSettings?.shortCustomDisclaimer,
    defaultFinanceTerms: config.defaultFinanceTerms ?? (vehicleType === 'New' ? drSettings?.financing?.defaultTermNew : drSettings?.financing?.defaultTermUsed),
    availableFinanceTermsByTier: config.availableFinanceTermsByTier ?? getFinanceTermsByTier(drSettings),
    disableUDS: !showUDS || supportedDeals.length === 0,
    hideSendOfferCTA: !isVDPLeadFormEnabled
  };
}
export { applyDrSettings as default };
